// @system — Card primitives (shadcn/ui pattern)
// Uses brand CSS variables for consistent theming.
// Reduced padding (p-4 instead of p-6) for modern density.
import { cn } from '@/app/lib/@system/utils'

/** @typedef {import('react').HTMLAttributes<HTMLDivElement>} CardProps */

/** @param {CardProps} props */
function Card({ className = '', ...props }) {
  return (
    <div
      className={cn(
        'rounded-lg bg-[var(--brand-surface)] text-[var(--brand-text)] shadow-sm',
        className
      )}
      {...props}
    />
  )
}

/** @param {CardProps} props */
function CardHeader({ className = '', ...props }) {
  return <div className={cn('flex flex-col space-y-1 p-4 pb-2', className)} {...props} />
}

/**
 * @typedef {object} CardTitleOwnProps
 * @property {'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'} [as] - Heading level (default h2)
 * @typedef {CardTitleOwnProps & import('react').HTMLAttributes<HTMLHeadingElement>} CardTitleProps
 */

// <h2> by default: cards sit directly under the page <h1> (heading order); `as="h3"` under an <h2> section.
/** @param {CardTitleProps} props */
function CardTitle({ className = '', children, as: Tag = 'h2', ...props }) {
  return (
    <Tag className={cn('text-base font-semibold leading-tight tracking-tight', className)} {...props}>
      {children}
    </Tag>
  )
}

/** @param {import('react').HTMLAttributes<HTMLParagraphElement>} props */
function CardDescription({ className = '', ...props }) {
  return <p className={cn('text-sm text-[var(--brand-text-secondary)]', className)} {...props} />
}

/** @param {CardProps} props */
function CardContent({ className = '', ...props }) {
  return <div className={cn('p-4 pt-0', className)} {...props} />
}

/** @param {CardProps} props */
function CardFooter({ className = '', ...props }) {
  return <div className={cn('flex items-center p-4 pt-0', className)} {...props} />
}

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter }
